import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Box,
	HStack,
	Stack,
	Tooltip,
} from '@chakra-ui/react';
import { mdiPlus } from '@mdi/js';
import { ColumnDef } from '@tanstack/react-table';
import { editCreateEnv } from '@xmcloud/core/messages/en';
import { IGetEnvironmentResponseV2 } from 'app/models/environmentModel';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useLocalStorage from 'use-local-storage';
import { useProjectDetails, useSharedState } from '../../../../@xmcloud/hooks';
import { IS_SM_SCREEN } from '../../../../@xmcloud/hooks/context/useSharedState';
import { pageSize } from '../../../../@xmcloud/utils/constants';
import { useGetEnvironmentsByQuery } from '../../../services/environments';
import {
	Footer,
	Icon,
	LoadingSkeleton,
	RenderPlaceholder,
	Table,
	Text14B700,
	TightTag,
	XMCSButton,
} from '../../../shared-components';
import { Main as CreateEnvironment } from '../../create-edit-environment/Main';
import { EnvironmentTypesEnum } from '../../create-project/helpers';
import { useEnvironmentsTableColumns } from '../columns/environments';
import { DeleteEnvironment } from '../components/DeleteEnvironment';
import { useGetEnvType } from '../hooks/useGetEnvType';

const { addCm, addEh, noCmEnvTooltipMsg } = editCreateEnv;

const headers = ['Type', 'Name', 'ID', 'Date created', ''];

const { CM } = EnvironmentTypesEnum;

const CM_ACCORDION_COLLAPSED_PREFIX = 'cm-accordion-collapsed';

const getCmAccordionStorageKey = (projectId: string | undefined) =>
	`${CM_ACCORDION_COLLAPSED_PREFIX}-${projectId ?? 'default'}`;

const AuthoringEnvironmentAccordion: React.FC<{
	name: string;
	tenantType: string;
	children: React.ReactNode;
}> = ({ name, tenantType, children }) => {
	return (
		<AccordionItem
			sx={{
				border: 'none',
				boxShadow: 'none',
				_before: { content: 'none' },
			}}
		>
			<AccordionButton minH="12">
				<HStack flex="1" spacing="2" textAlign="left">
					<Text14B700 text={name} wordBreak="break-word" />
					{tenantType === 'prod' && (
						<TightTag flexShrink={0}>Production</TightTag>
					)}
				</HStack>
				<AccordionIcon />
			</AccordionButton>

			<AccordionPanel mt={['5', '1', '1', '1']}>
				{children}
			</AccordionPanel>
		</AccordionItem>
	);
};

/** Order-insensitive equality of unique id sets (ignores duplicate entries in storage). */
const collapsedListsEquivalent = (a: string[], b: string[]) => {
	const as = new Set(a);
	const bs = new Set(b);
	if (as.size !== bs.size) return false;
	return Array.from(as).every((id) => bs.has(id));
};

export const EnvironmentsPage: React.FC = () => {
	const { isCmEnv, isEhEnv, envType, projectId } = useGetEnvType();
	const [collapsedCmEnvIds, setCollapsedCmEnvIds] = useLocalStorage<string[]>(
		getCmAccordionStorageKey(projectId),
		[],
	);
	const [openCreateModal, setOpenCreateModal] = useState(false);

	const { state: isSmScreen } = useSharedState(IS_SM_SCREEN, false);
	const { project } = useProjectDetails(projectId!);

	const buttonText = isEhEnv ? addEh : addCm;

	const { data: environments, isLoading: isEnvironmentsLoading } =
		useGetEnvironmentsByQuery({
			type: envType as EnvironmentTypesEnum,
			projectId: projectId!,
		});

	const { data: cmEnvironments, isLoading: isCmEnvironmentsLoading } =
		useGetEnvironmentsByQuery({
			type: CM,
			projectId: projectId!,
			_enabled: isEhEnv,
		});

	const {
		columns,
		editModal,
		setEditModal,
		environmentRef,
	}: {
		columns: ColumnDef<IGetEnvironmentResponseV2, any>[];
		editModal: boolean;
		setEditModal: React.Dispatch<React.SetStateAction<boolean>>;
		environmentRef: React.MutableRefObject<IGetEnvironmentResponseV2>;
	} = useEnvironmentsTableColumns(envType, project?.isLocked);

	const envLength = environments?.length || 0;
	const cmEnvLength = cmEnvironments?.length || 0;

	const environmentsByCmId = useMemo(() => {
		if (!environments) return {};

		return environments.reduce<Record<string, IGetEnvironmentResponseV2[]>>(
			(acc, env) => {
				const cmId = env.editingHostEnvironmentDetails?.cmEnvironmentId;
				if (!cmId) return acc;

				acc[cmId] ||= [];
				acc[cmId].push(env);
				return acc;
			},
			{},
		);
	}, [environments]);

	const cmEnvironmentsWithEh = useMemo(() => {
		if (!cmEnvironments) return [];

		return cmEnvironments.filter(
			(cmEnv) => environmentsByCmId[cmEnv.id]?.length,
		);
	}, [cmEnvironments, environmentsByCmId]);

	const cmAccordionOnceRef = useRef({ projectId, ran: false });
	if (cmAccordionOnceRef.current.projectId !== projectId) {
		cmAccordionOnceRef.current = { projectId, ran: false };
	}

	useEffect(() => {
		if (!cmEnvironmentsWithEh.length) return;

		const isFirstRunForProject = !cmAccordionOnceRef.current.ran;

		setCollapsedCmEnvIds((prev) => {
			const prior = prev ?? [];
			const allIds = cmEnvironmentsWithEh.map((e) => e.id);
			const idSet = new Set(allIds);
			let pruned = prior.filter((id) => idSet.has(id));

			const allCollapsed =
				allIds.length > 0 && allIds.every((id) => pruned.includes(id));

			// If all environments are collapsed on initial load, expand the first one
			if (allCollapsed && isFirstRunForProject) {
				pruned = pruned.filter((id) => id !== allIds[0]);
			}

			if (collapsedListsEquivalent(pruned, prior)) {
				return prior;
			}
			return pruned;
		});

		cmAccordionOnceRef.current.ran = true;
	}, [cmEnvironmentsWithEh, setCollapsedCmEnvIds]);

	const isLoading = isEnvironmentsLoading || isCmEnvironmentsLoading;
	const disableCreateEhBtn = !cmEnvLength && isEhEnv;

	const collapsedIdSet = useMemo(
		() => new Set(collapsedCmEnvIds ?? []),
		[collapsedCmEnvIds],
	);

	const openAccordionIndexes = useMemo(
		() =>
			cmEnvironmentsWithEh
				.map((env, i) => (!collapsedIdSet.has(env.id) ? i : -1))
				.filter((i): i is number => i >= 0),
		[cmEnvironmentsWithEh, collapsedIdSet],
	);

	/**
	 * Hidden first AccordionItem (Chakra index 0) so real panels use 1..n.
	 * Chakra v2 controlled accordion + allowMultiple can mis-open every panel after remount
	 * when the only open index would be 0 (first authoring env only expanded).
	 */
	const chakraAccordionOpenIndexes = useMemo(
		() => openAccordionIndexes.map((i) => i + 1),
		[openAccordionIndexes],
	);

	const handleAccordionChange = useCallback(
		(expandedIndexes: number | number[]) => {
			if (!Array.isArray(expandedIndexes)) return;

			const logicalExpandedIndexes = expandedIndexes
				.filter((i) => i > 0)
				.map((i) => i - 1);

			const expanded = new Set(logicalExpandedIndexes);
			const collapsed = cmEnvironmentsWithEh
				.filter((_, i) => !expanded.has(i))
				.map((e) => e.id);

			setCollapsedCmEnvIds(collapsed);
		},
		[cmEnvironmentsWithEh, setCollapsedCmEnvIds],
	);

	if (isLoading) {
		return <LoadingSkeleton amount={pageSize + 1} />;
	}

	return (
		<Stack>
			{editModal && (
				<DeleteEnvironment
					{...{
						openModal: editModal,
						setOpenModal: setEditModal,
						environmentDetails: environmentRef.current,
					}}
				/>
			)}
			<HStack justify="flex-end" py={isSmScreen ? 3 : 5}>
				{!isSmScreen && (
					<CreateButton
						setOpenCreateModal={setOpenCreateModal}
						isDisabled={disableCreateEhBtn}
						buttonText={buttonText}
					/>
				)}
			</HStack>

			{!envLength ? (
				<RenderPlaceholder item="environments" />
			) : envType === CM ? (
				<Table
					columns={columns}
					tableData={environments}
					dataTestId="environments-table"
					showPagination={envLength > pageSize}
					mb={5}
					headers={headers}
				/>
			) : (
				<Accordion
					allowMultiple
					index={chakraAccordionOpenIndexes}
					onChange={handleAccordionChange}
					mt="8"
					mb={['20', '8', '8', '8']}
				>
					{/* Sentinel AccordionItem to prevent ChakraUI bug where all accordions items are shown
					when switching between CM and EH tabs if only the first accordion is initially expanded */}
					<AccordionItem
						key="eh-accordion-chakra-index-placeholder"
						sx={{ display: 'none' }}
					>
						<AccordionButton aria-hidden tabIndex={-1}>
							<span />
						</AccordionButton>
						<AccordionPanel />
					</AccordionItem>
					{cmEnvironmentsWithEh.map((cmEnv) => {
						const linkedEhEnvironments =
							environmentsByCmId[cmEnv.id];

						return (
							<AuthoringEnvironmentAccordion
								key={cmEnv.id}
								name={cmEnv.name}
								tenantType={cmEnv.tenantType}
							>
								<Table
									columns={columns}
									tableData={linkedEhEnvironments}
									dataTestId="environments-table"
									showPagination={
										linkedEhEnvironments.length > pageSize
									}
									mb={5}
									headers={headers}
								/>
							</AuthoringEnvironmentAccordion>
						);
					})}
				</Accordion>
			)}

			{!!isSmScreen && (
				<Footer>
					<CreateButton
						setOpenCreateModal={setOpenCreateModal}
						isDisabled={disableCreateEhBtn}
						buttonText={buttonText}
					/>
				</Footer>
			)}

			{openCreateModal && (
				<CreateEnvironment
					isOpen={openCreateModal}
					onClose={() => setOpenCreateModal(false)}
					isCreate
					isCm={isCmEnv}
					environment={environments?.[0]}
				/>
			)}
		</Stack>
	);
};

export const CreateButton: React.FC<{
	setOpenCreateModal: (a: boolean) => void;
	isDisabled: boolean;
	buttonText: string;
}> = ({ setOpenCreateModal, isDisabled, buttonText }) => (
	<Tooltip label={noCmEnvTooltipMsg} isDisabled={!isDisabled}>
		<Box as="span" display="inline-block">
			<XMCSButton
				{...{
					text: buttonText,
					onClick: () => setOpenCreateModal(true),
					leftIcon: <Icon path={mdiPlus} />,
					isDisabled,
				}}
			/>
		</Box>
	</Tooltip>
);
